import { ChevronDown, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';

import { useTranslation } from '../use-translation.js';

const PANEL_OFFSET_PX = 4;
const PANEL_MIN_WIDTH_PX = 180;
const HALF_RATIO = 0.5;

export interface SelectOption {
  readonly value: string | number;
  readonly label: string;
}

export interface CustomSelectProps {
  readonly id?: string;
  readonly value: string | number;
  readonly onChange: (value: string) => void;
  readonly options: readonly SelectOption[];
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly triggerClassName?: string;
  readonly label?: string;
  readonly icon?: React.ReactNode;
  readonly ariaLabel?: string;
  readonly searchable?: boolean;
  readonly searchPlaceholder?: string;
  readonly hideDropdownIcon?: boolean;
}

export function CustomSelect({
  id,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  className = '',
  triggerClassName = '',
  label,
  icon,
  ariaLabel,
  searchable = false,
  searchPlaceholder,
  hideDropdownIcon = false,
}: CustomSelectProps): JSX.Element {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selectedItemRef = useRef<HTMLButtonElement>(null);
  const [panelRect, setPanelRect] = useState<{
    readonly top: number;
    readonly left: number;
    readonly width: number;
  } | null>(null);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));
  const displayPlaceholder = placeholder ?? t('common.select');
  const displaySearchPlaceholder = searchPlaceholder ?? t('common.search');

  const filteredOptions = useMemo(() => {
    if (search.trim() === '') return options;
    const query = search.trim().toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(query));
  }, [options, search]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) === true) return;
      if (panelRef.current?.contains(target) === true) return;
      setIsOpen(false);
    }
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const updatePanelRect = (): void => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect === undefined) return;
    setPanelRect({ top: rect.bottom + PANEL_OFFSET_PX, left: rect.left, width: rect.width });
  };

  // The panel renders in a body portal so it escapes ancestor stacking
  // contexts — the sticky header is z-40 while page-level sticky bars sit at
  // z-50, which would otherwise paint over the dropdown. Its fixed position
  // is anchored to the trigger and re-measured while open.
  useEffect(() => {
    if (!isOpen) return undefined;
    window.addEventListener('resize', updatePanelRect);
    document.addEventListener('scroll', updatePanelRect, true);
    return () => {
      window.removeEventListener('resize', updatePanelRect);
      document.removeEventListener('scroll', updatePanelRect, true);
    };
  }, [isOpen]);

  // Open with the selected option centred in the panel — the native select
  // behaviour — instead of landing on the first option every time. The scroll
  // is applied to the panel only, so the page never jumps.
  useEffect(() => {
    if (!isOpen) return;
    const frame = requestAnimationFrame(() => {
      const item = selectedItemRef.current;
      const panel = panelRef.current;
      if (item === null || panel === null) return;
      const itemCenter = item.getBoundingClientRect().top + item.clientHeight * HALF_RATIO;
      const panelCenter = panel.getBoundingClientRect().top + panel.clientHeight * HALF_RATIO;
      panel.scrollTop += itemCenter - panelCenter;
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchable) {
      const id = setTimeout(() => {
        searchRef.current?.focus();
      }, 0);
      return () => {
        clearTimeout(id);
      };
    }
    return undefined;
  }, [isOpen, searchable]);

  const defaultTriggerClass =
    'flex w-full items-center justify-between gap-2 rounded-md border border-input bg-surface px-3 py-1.5 text-xs font-semibold text-foreground hover:border-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer shadow-xs transition-colors';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label !== undefined && (
        <label
          htmlFor={id}
          className="mb-1 text-[11px] font-semibold text-muted-foreground flex items-center gap-1.5"
        >
          {icon}
          {label}
        </label>
      )}
      {id !== undefined && (
        <select
          id={id}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          aria-label={ariaLabel ?? label}
          tabIndex={-1}
          className="sr-only"
        >
          {options.map((opt) => (
            <option key={`${String(opt.value)}-${opt.label}`} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}
      <button
        ref={triggerRef}
        id={id !== undefined ? `${id}-trigger` : undefined}
        type="button"
        disabled={disabled}
        {...(id === undefined && (ariaLabel !== undefined || label !== undefined)
          ? { 'aria-label': ariaLabel ?? label }
          : {})}
        aria-expanded={isOpen}
        onClick={() => {
          if (!disabled) {
            if (!isOpen) {
              if (searchable) setSearch('');
              updatePanelRect();
            }
            setIsOpen(!isOpen);
          }
        }}
        className={triggerClassName !== '' ? triggerClassName : defaultTriggerClass}
      >
        <span className="truncate">
          {selectedOption !== undefined ? selectedOption.label : displayPlaceholder}
        </span>
        {!hideDropdownIcon && (
          <ChevronDown
            className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        )}
      </button>

      {isOpen &&
        panelRect !== null &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-80 max-h-60 min-w-[180px] overflow-y-auto rounded-md border border-border bg-surface p-1 shadow-overlay text-xs select-none"
            style={{
              top: panelRect.top,
              left: panelRect.left,
              width: Math.max(panelRect.width, PANEL_MIN_WIDTH_PX),
            }}
          >
            {searchable && (
              <div className="sticky top-0 z-10 mb-1 border-b border-border bg-surface p-1">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                    }}
                    placeholder={displaySearchPlaceholder}
                    className="ibe-control w-full py-1 pl-7 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </div>
              </div>
            )}
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-muted-foreground">{t('common.noOptions')}</div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = String(option.value) === String(value);
                return (
                  <button
                    key={String(option.value)}
                    ref={isSelected ? selectedItemRef : undefined}
                    type="button"
                    onClick={() => {
                      onChange(String(option.value));
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className={`flex w-full items-center justify-between rounded-sm px-3 py-1.5 text-left text-xs font-medium transition-colors ${
                      isSelected
                        ? 'bg-primary-container text-on-primary-container font-bold'
                        : 'text-secondary-foreground hover:bg-muted'
                    }`}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && <span className="text-on-primary-container font-bold">✓</span>}
                  </button>
                );
              })
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
